import React, { useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { format } from "date-fns";
import { useToggle } from "usehooks-ts";
import _orderBy from "lodash/orderBy";
import _sortBy from "lodash/sortBy";
import { useHorizontalScrollControl } from "shared-hooks";
import { useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import ProfileButton from "pages/Profile/components/common/ProfileButton";
import { useGetCredits } from "pages/Profile/hooks/useGetCredits";
import { CREDITS_FULL_PATH } from "shared/constants";
import CreditHeading from "../../../../models/Credits/CreditHeading";

interface Props {
  isEdit?: boolean;
}

const FURTHER_TAB_ID = -1;
const PAGINATION_STEP = 10;

const CreditsCard = ({ isEdit }: Props) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<number>();
  const [showTabs, toggleShowTabs] = useToggle(true);
  const [viewInDateOrder, toggleViewInDateOrder] = useToggle(false);
  const [showBy, setShowBy] = useState(PAGINATION_STEP);

  const { credits, creditsHeadings, isLoading } = useGetCredits();

  const { visibleCreditsHeader, furtherCreditsIds } = useMemo(() => {
    const headers = creditsHeadings?.reduce<{
      visibleCreditsHeader: CreditHeading[];
      furtherCreditsIds: number[];
    }>(
      (acc, credit) => {
        if (credit.isHidden) {
          return {
            ...acc,
            furtherCreditsIds: [...acc.furtherCreditsIds, credit.creditType.id],
          };
        }

        return {
          ...acc,
          visibleCreditsHeader: [...acc.visibleCreditsHeader, credit],
        };
      },
      {
        visibleCreditsHeader: [],
        furtherCreditsIds: [],
      }
    ) || { visibleCreditsHeader: [], furtherCreditsIds: [] };

    return {
      ...headers,
      visibleCreditsHeader: _sortBy(headers.visibleCreditsHeader, "position"),
    };
  }, [creditsHeadings]);

  const { scrollRef, showLeft, showRight, scroll } = useHorizontalScrollControl(
    100,
    [showTabs, visibleCreditsHeader]
  );

  const creditsToShow = useMemo(() => {
    let collection = credits;

    if (activeTab === FURTHER_TAB_ID && showTabs) {
      collection = credits?.filter((credit) =>
        furtherCreditsIds.includes(credit.typeId)
      );
    }

    if (activeTab !== FURTHER_TAB_ID && showTabs) {
      collection = credits?.filter((credit) => credit.typeId === activeTab);
    }

    return _orderBy(
      collection,
      viewInDateOrder ? "dateTo" : "position",
      viewInDateOrder ? "desc" : "asc"
    );
  }, [activeTab, credits, furtherCreditsIds, showTabs, viewInDateOrder]);

  useEffect(() => {
    setShowBy(PAGINATION_STEP);
  }, [activeTab, showTabs]);

  useEffect(() => {
    if (!isLoading && !activeTab) {
      setActiveTab(visibleCreditsHeader[0]?.creditType.id || FURTHER_TAB_ID);
    }
  }, [visibleCreditsHeader, activeTab, isLoading]);

  const showViewMore = showBy < (creditsToShow?.length || 0);
  const showViewLess = showBy > PAGINATION_STEP;

  return (
    <ProfileCard
      isEdit={isEdit}
      isLoading={isLoading}
      editToPath={CREDITS_FULL_PATH}
    >
      <h2 className="mb-4 text-m uppercase leading-6 dark:text-white">
        {t("performer:profile.credits.title")}
      </h2>

      <div className="mb-3 flex gap-1.5 text-s dark:text-white/70">
        {!!visibleCreditsHeader.length && (
          <>
            <button
              className="decoration-white/30 hover:text-white"
              onClick={toggleShowTabs}
            >
              {showTabs
                ? t("performer:profile.credits.viewWithoutTabs")
                : t("performer:profile.credits.viewWithTabs")}
            </button>
            <span className="opacity-30">|</span>
          </>
        )}
        <button
          className="decoration-white/30 hover:text-white"
          onClick={toggleViewInDateOrder}
        >
          {viewInDateOrder
            ? t("performer:profile.credits.viewInPerformerOrder")
            : t("performer:profile.credits.viewInDateOrder")}
        </button>
      </div>

      {showTabs && !!visibleCreditsHeader.length && (
        <div className="relative">
          {showLeft && (
            <button
              onClick={() => scroll("left")}
              aria-label="Scroll tabs left"
              className="absolute left-0 top-0 h-full w-8 bg-[linear-gradient(90deg,_rgba(0,0,0,0.2)_0%,_rgba(0,0,0,0)_100%)]"
            />
          )}
          <div className="mb-3 flex gap-1 overflow-auto pb-1" ref={scrollRef}>
            {visibleCreditsHeader?.map((creditHead) => (
              <ProfileButton
                isText
                key={creditHead.creditType.id}
                onClick={() => setActiveTab(creditHead?.creditType?.id)}
                className={cx(
                  "h-7 text-nowrap py-1.5 font-normal",
                  activeTab === creditHead.creditType.id
                    ? "!bg-color-brand-one text-white dark:!bg-white dark:text-color-brand-one"
                    : "dark:text-white/70"
                )}
              >
                {creditHead.creditType.name}
              </ProfileButton>
            ))}
            {!!furtherCreditsIds.length && (
              <ProfileButton
                isText
                onClick={() => setActiveTab(FURTHER_TAB_ID)}
                className={cx(
                  "h-7 py-1.5 font-normal",
                  activeTab === FURTHER_TAB_ID
                    ? "!bg-color-brand-one text-white dark:!bg-white dark:text-color-brand-one"
                    : "dark:text-white/70"
                )}
              >
                {t("common:tabs.label.further")}
              </ProfileButton>
            )}
          </div>
          {showRight && (
            <button
              onClick={() => scroll("right")}
              aria-label="Scroll tabs right"
              className="absolute right-0 top-0 h-full w-8 bg-[linear-gradient(270deg,_rgba(0,0,0,0.2)_0%,_rgba(0,0,0,0)_100%)]"
            />
          )}
        </div>
      )}

      <div className="space-y-2">
        {creditsToShow?.map((item, index) => {
          return (
            <div
              key={item.id}
              className={cx("border-b border-white/20 last:border-0", {
                hidden: index >= showBy,
              })}
              data-testid={dataTestIds.profile["Credits.CreditSection"]}
            >
              <h3 className="mb-1.5 text-s font-semibold uppercase dark:text-white">
                {item.name}
              </h3>
              <div className="grid grid-cols-2 justify-items-start gap-y-1 pb-3 text-s">
                <div>
                  <span className="dark:text-white/50">
                    {t("common:label.type")}:
                  </span>{" "}
                  <span className="dark:text-white/70">{item.type}</span>
                </div>
                <div>
                  <span className="dark:text-white/50">
                    {t("common:label.year.cap")}:
                  </span>{" "}
                  <span className="dark:text-white/70">
                    {item.dateTo &&
                      format(
                        new Date(item.dateTo || ""),
                        item.dateToFormat || "y"
                      )}
                  </span>
                </div>
                <div>
                  <span className="dark:text-white/50">
                    {t("common:label.role")}:
                  </span>{" "}
                  <span className="dark:text-white/70">{item.role}</span>
                </div>
                <div>
                  <span className="dark:text-white/50">
                    {t("common:label.director")}:
                  </span>{" "}
                  <span className="dark:text-white/70">{item.director}</span>
                </div>
                <div>
                  <span className="dark:text-white/50">
                    {t("common:label.company")}:
                  </span>{" "}
                  <span className="dark:text-white/70">{item.company}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-1.5 pt-4 text-sm dark:text-gray-300">
        {showViewMore && (
          <button
            className="hover:text-white disabled:pointer-events-none disabled:opacity-30"
            onClick={() => setShowBy(showBy + PAGINATION_STEP)}
          >
            {t("common:button.label.viewMore")}
          </button>
        )}
        {showViewMore && <span className="opacity-30">|</span>}
        {showViewLess && (
          <button
            className="hover:text-white disabled:pointer-events-none disabled:opacity-30"
            onClick={() => setShowBy(PAGINATION_STEP)}
          >
            {t("common:button.label.viewLess")}
          </button>
        )}
        {showViewMore && showViewLess && <span className="opacity-30">|</span>}
        {showViewMore && (
          <button
            className="hover:text-white disabled:pointer-events-none disabled:opacity-30"
            onClick={() => setShowBy(creditsToShow?.length || PAGINATION_STEP)}
          >
            {t("common:button.label.viewAll")}
          </button>
        )}
      </div>
    </ProfileCard>
  );
};

export default CreditsCard;
